import { getStartOfToday } from "@/common/helpers/date-input";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { useIsMobile } from "@/common/hooks/use-mobile";
import { Button, ButtonVariant } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import type { DateRange } from "react-day-picker";

const MAX_RANGE_MONTHS = 6;

// Same calendar day N months later, clamped to the end of shorter months (e.g. Aug 31 -> Feb 28).
function addMonthsClamped(date: Date, months: number): Date {
  const targetMonth = date.getMonth() + months;
  const lastDayOfTargetMonth = new Date(date.getFullYear(), targetMonth + 1, 0).getDate();
  return new Date(date.getFullYear(), targetMonth, Math.min(date.getDate(), lastDayOfTargetMonth));
}

interface DateRangePickerProps {
  selected: DateRange | undefined;
  onSelect: (range?: DateRange) => void;
}

export function DateRangePicker({ selected, onSelect }: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [today, setToday] = React.useState(getStartOfToday);
  const isMobile = useIsMobile();
  const latestEndDate = selected?.from ? addMonthsClamped(selected.from, MAX_RANGE_MONTHS) : undefined;
  const isChoosingEndDate = selected?.from && !selected.to;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setToday(getStartOfToday());
    }
    setOpen(nextOpen);
  };

  const fromLabel = selected?.from ? formatDateToLocale(selected.from.toISOString(), { dateOnly: true }) : "???";
  const toLabel = selected?.to ? formatDateToLocale(selected.to.toISOString(), { dateOnly: true }) : "???";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant={ButtonVariant.Input} className="w-fit justify-end">
          <CalendarIcon /> {fromLabel} - {toLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="range"
          resetOnSelect
          selected={selected}
          defaultMonth={selected?.from}
          onSelect={onSelect}
          numberOfMonths={isMobile ? 1 : 2}
          modifiers={{
            available_range: isChoosingEndDate && latestEndDate ? { from: selected.from, to: latestEndDate } : false,
          }}
          modifiersClassNames={{
            available_range: "[&:not([data-selected])]:bg-primary/10",
          }}
          disabled={[
            { before: today },
            ...(isChoosingEndDate ? [{ before: selected.from }] : []),
            ...(isChoosingEndDate && latestEndDate ? [{ after: latestEndDate }] : []),
          ]}
        />
      </PopoverContent>
    </Popover>
  );
}
